import { useEffect } from 'react';
import PublicNav from '@/components/PublicNav';
import Logotype from '@/components/shared/Logotype';

const APPS = [
  {
    key: 'uncode',
    tagline: 'Chaos into Clarity',
    accent: '#E85D24',
    description: 'Where raw thought lands before it becomes anything. Heat-coded by urgency, split by domain. Capture first, classify later. The system figures out what you meant.',
  },
  {
    key: 'giraffe',
    tagline: 'The Journey Unraveled',
    accent: '#10B981',
    description: 'Trips as living documents. Plan logistics, compose photographic fragments, then distill the whole thing into a published memoir. The raw material and the finished artifact in one place.',
  },
  {
    key: 'framed',
    tagline: 'A Life in Technicolor',
    accent: '#D4537E',
    description: 'Photography organized into collections, curated into galleries, composed into postcards. Every image has a place, a publish state, and a reason to exist.',
  },
  {
    key: 'cult',
    tagline: 'Manifest of a Movement',
    accent: '#F5A623',
    description: 'The philosophical surface. Mantras with voices from the thinkers who shaped them. Essays, poems, paintings. Published works that express a worldview.',
  },
  {
    key: 'soulsync',
    tagline: 'Architecting the Self',
    accent: '#4B5FD4',
    description: 'Codex entries that crystallize what you know about yourself. Daily pulses, a reading library, and a raw chronicle. The inner life made navigable.',
  },
  {
    key: 'zephyr',
    tagline: 'The Echo of a Life Less Ordinary',
    accent: '#6B35B8',
    description: 'The AI layer that threads through every app. Ask it anything about your own data. It reads the codex, the trips, the tasks, and reasons across all of them.',
  },
  {
    key: 'arx',
    tagline: 'The Archive of Life',
    accent: '#C0A96A',
    description: 'Passports, contracts, credentials, insurance. The administrative substrate of a life. Encrypted, searchable, behind a second passphrase.',
  },
  {
    key: 'verve',
    tagline: 'Forging Freedom',
    accent: '#FF0042',
    description: 'Daily food logging with macro tracking, body measurements over time, and structured training programs. The body as a system with inputs and outputs.',
  },
  {
    // Tenth slot — xeNT, the aesthetic surface. Full app build is a Wave 1
    // spec coming next; this card is gallery-display only. The tile uses a
    // pure black background and a white accent because the graphite
    // labyrinth logo needs maximum contrast to read against its own palette.
    // Every other card uses a saturated accent against the shared
    // rgba(255,255,255,0.03) backdrop; xeNT is deliberately the exception.
    // Brand casing is lowercase e: 'xeNT' not 'XeNT'.
    key: 'xent',
    tagline: 'Eclectically Eccentric',
    accent: '#ffffff',
    tileBg: '#000000',
    description: 'The aesthetic surface: architecture, garments, art, design, accessories, objects from the made world. Curated for insight and impact.',
  },
];

export default function LabsGallery() {
  useEffect(() => {
    document.title = 'Zephyr OS - Labs';
  }, []);

  return (
    <div className="min-h-screen bg-[#0a0a0f] pt-4 md:pt-16 pb-20">
      <PublicNav />

      {/* Zephyr OS section */}
      <div className="max-w-[640px] mx-auto px-4 md:px-8 text-center mb-12">
        <Logotype app="zephyr" size="header" />
        <p style={{
          fontFamily: "'Inter', sans-serif",
          fontSize: 15,
          fontWeight: 400,
          color: '#b0b4c0',
          lineHeight: 1.7,
          marginTop: 20,
        }}>
          Zephyr is the operating system and AI agent I built using Awakening Interface. It was the singular biggest impact on how I work with AI: it allowed me to build an entire ecosystem based on how I see the world and what I need to live a better life.
        </p>
        <p style={{
          fontFamily: "'Inter', sans-serif",
          fontSize: 15,
          fontWeight: 400,
          color: '#b0b4c0',
          lineHeight: 1.7,
          marginTop: 16,
        }}>
          Below is the list of apps I built in the 7 weeks I have been in Japan, in between traveling, expanding my horizons, experiencing the new.
        </p>
        <p style={{
          fontFamily: "'Inter', sans-serif",
          fontSize: 17,
          fontWeight: 500,
          color: '#f0f0f5',
          lineHeight: 1.4,
          marginTop: 20,
        }}>
          What could you do?
        </p>
      </div>

      {/* Labs grid */}
      <div className="max-w-[1000px] mx-auto px-4 md:px-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {APPS.map(app => (
            <div
              key={app.key}
              className="transition-all duration-300"
              style={{
                background: app.tileBg || 'rgba(255,255,255,0.03)',
                border: `1px solid ${app.accent}59`, // ~0.35 opacity
                borderRadius: 12,
                padding: 24,
                minHeight: 180,
                position: 'relative',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = `${app.accent}80`; }} // ~0.50
              onMouseLeave={e => { e.currentTarget.style.borderColor = `${app.accent}59`; }}
            >
              <Logotype app={app.key} size="dashboard" />
              <p style={{
                fontFamily: "'Inter', sans-serif",
                fontSize: 14,
                fontWeight: 500,
                textTransform: 'uppercase',
                letterSpacing: '1.5px',
                color: app.accent,
                marginTop: 16,
              }}>
                {app.tagline}
              </p>
              <p style={{
                fontFamily: "'Inter', sans-serif",
                fontSize: 14,
                fontWeight: 400,
                color: '#b0b4c0',
                lineHeight: 1.6,
                marginTop: 12,
              }}>
                {app.description}
              </p>
              {app.comingSoon && (
                <span style={{
                  position: 'absolute',
                  top: 16,
                  right: 16,
                  fontFamily: "'Inter', sans-serif",
                  fontSize: 9,
                  fontWeight: 500,
                  letterSpacing: '0.15em',
                  textTransform: 'uppercase',
                  color: 'rgba(255,255,255,0.45)',
                  border: '1px solid rgba(255,255,255,0.18)',
                  padding: '3px 8px',
                  borderRadius: 999,
                }}>
                  Coming soon
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
